import { useState } from 'react'
import { partyApi, PartyStatus, CommandType } from '../lib/api'

interface CommandBarProps {
  partyId: string
  status: PartyStatus
  onCommandSent: () => void
}

interface CommandButton {
  type: CommandType
  label: string
  visibleWhen: PartyStatus[]
  className: string
  confirm?: string
}

const commands: CommandButton[] = [
  {
    type: 'start',
    label: 'Start',
    visibleWhen: ['waiting', 'stopped'],
    className: 'bg-green-600 hover:bg-green-700 text-white',
  },
  {
    type: 'pause',
    label: 'Pause',
    visibleWhen: ['active'],
    className: 'bg-yellow-500 hover:bg-yellow-600 text-white',
  },
  {
    type: 'resume',
    label: 'Resume',
    visibleWhen: ['paused'],
    className: 'bg-green-600 hover:bg-green-700 text-white',
  },
  {
    type: 'stop',
    label: 'Stop',
    visibleWhen: ['active', 'paused'],
    className: 'bg-red-600 hover:bg-red-700 text-white',
    confirm: 'Stop streaming for all participants?',
  },
]

export default function CommandBar({ partyId, status, onCommandSent }: CommandBarProps) {
  const [loading, setLoading] = useState<string | null>(null)

  if (status === 'ended') return null

  const handleCommand = async (cmd: CommandButton) => {
    if (cmd.confirm && !confirm(cmd.confirm)) return
    setLoading(cmd.type)
    try {
      await partyApi.sendCommand(partyId, cmd.type)
      onCommandSent()
    } catch {
      // polling will catch up
    } finally {
      setLoading(null)
    }
  }

  const visibleCommands = commands.filter((c) => c.visibleWhen.includes(status))

  if (visibleCommands.length === 0) return null

  return (
    <div className="bg-white shadow rounded-lg px-5 py-3 flex items-center gap-3">
      <span className="text-sm font-medium text-gray-600 mr-2">Controls</span>
      {visibleCommands.map((cmd) => (
        <button
          key={cmd.type}
          onClick={() => handleCommand(cmd)}
          disabled={loading !== null}
          className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${cmd.className}`}
        >
          {loading === cmd.type ? (
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : null}
          {cmd.label}
        </button>
      ))}
    </div>
  )
}
